import React, { useEffect, useRef, useState } from "react";
import { resolvePreviewSize, useEmailPreviewDisplay } from "./previewDisplay";
import "./AutoHeightEmailFrame.css";

// One shared formula for "the monitor's available screen height" (per the
// user's explicit request: every Editor and Settings preview should use
// this), reused by every caller instead of each one picking its own number.
// `--elakay-header-height` is the app's own live-measured header height
// (Layout/useHeaderHeightVar.js, set on every page) -- not an invented
// constant. 40px is a small safe margin, matching the codebase's own
// convention (see includes/pageInside/howWeDoIt/howWeDoItSidebar.css's
// `calc(100vh - var(--elakay-header-height, 90px) - 30px)`).
export const EMAIL_PREVIEW_VIEWPORT_HEIGHT = "calc(100vh - var(--elakay-header-height, 90px) - 40px)";

// Single shared "render this email HTML, sized to the monitor, no
// clipping" primitive. Used by Build Email's Preview step
// (EmailPreviewPane.jsx), the Template Gallery preview
// (TemplateGallery.jsx) and the Visual Content Settings live preview
// (VisualContentSettings.jsx) -- one render engine, not three: every one
// of them just feeds this component real HTML from the exact same
// server-side renderBlocksToHtml()/renderProductCard() functions.
//
// The IFRAME itself owns both height and scrolling (by explicit request):
// its height is the monitor's available space (or Preview Display
// Settings' configured fixed/min/max value), never the document's own
// content height -- an iframe can't size itself to its content via CSS
// alone (height:auto on an iframe just falls back to the ~150px browser
// default, since it never sees inside a cross-document `srcDoc` box), and
// measuring that content height in JS to reset the SAME iframe's outer
// height was the previous approach here. This is simpler and needs no
// measurement at all: the height is just "how much screen this preview is
// allowed to occupy," and the iframe scrolls its own document internally
// (`overflowY`, `scrolling="auto"`) whenever the real content is taller
// than that -- so a very long email never grows the page around it, it
// scrolls in place inside its own device frame. The outer wrapper only
// provides the border/shadow/radius chrome and owns WIDTH -- it shrinks to
// fit the iframe's height exactly (no explicit height of its own), so
// there is exactly one scroll container here, not two.
//
// `viewportHeight` (optional, a CSS length like "calc(100vh - 120px)"):
// overrides the measured monitor height with a caller-supplied one (used
// when the caller already knows its own exact usable height, e.g. a modal
// whose header height is fixed).
const AutoHeightEmailFrame = ({ html, width, title = "Email preview", minHeight = 700, viewportHeight, previewType = "iframe", previewDisplay }) => {
  const wrapperRef = useRef(null);
  const [availableHeight, setAvailableHeight] = useState(null);
  const displaySettings = useEmailPreviewDisplay(previewDisplay);
  const resolved = resolvePreviewSize(displaySettings, previewType, { width: typeof width === "number" ? width : undefined });

  // Default preview height is the monitor space that is genuinely still
  // available below this viewport. This measures the rendered toolbar,
  // modal header and page header instead of guessing one subtraction.
  // Skipped entirely when the Height mode is "Fixed" (`resolved.height`
  // set) -- a fixed height is an explicit, literal choice that should
  // never be recomputed from the screen.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || viewportHeight || resolved.height) return undefined;
    const modalBody = wrapper.closest(".emk-preview-modal .modal-body");
    const stage = wrapper.parentElement?.classList.contains("emk-preview-stage") ? wrapper.parentElement : null;
    const measureAvailable = () => {
      const top = wrapper.getBoundingClientRect().top;
      let bottom = window.innerHeight - 24;
      if (modalBody) {
        const bodyStyle = window.getComputedStyle(modalBody);
        bottom = Math.min(bottom, modalBody.getBoundingClientRect().bottom - (parseFloat(bodyStyle.paddingBottom) || 0));
      }
      if (stage) {
        const stageStyle = window.getComputedStyle(stage);
        bottom -= parseFloat(stageStyle.paddingBottom) || 0;
      }
      const next = Math.max(minHeight, Math.floor(bottom - top));
      setAvailableHeight((current) => current === next ? current : next);
    };
    measureAvailable();
    window.addEventListener("resize", measureAvailable);
    const observer = "ResizeObserver" in window ? new ResizeObserver(measureAvailable) : null;
    if (wrapper.parentElement && observer) observer.observe(wrapper.parentElement);
    if (modalBody && observer) observer.observe(modalBody);
    return () => {
      window.removeEventListener("resize", measureAvailable);
      observer?.disconnect();
    };
  }, [minHeight, resolved.height, viewportHeight]);

  const monitorHeight = viewportHeight || (availableHeight ? `${availableHeight}px` : EMAIL_PREVIEW_VIEWPORT_HEIGHT);

  // Fixed mode: the admin's literal configured height, full stop. Every
  // other mode (auto/min/responsive) fills the monitor's available height
  // and clamps to it with CSS min() -- combining BOTH caps rather than one
  // replacing the other, so a configured maxHeight larger than the actual
  // screen (or simply generous) can never push the preview taller than
  // what's really visible.
  const iframeHeightStyle = resolved.height
    ? { height: resolved.height }
    : { height: monitorHeight, maxHeight: `min(${resolved.maxHeight || monitorHeight}, ${monitorHeight})` };

  return (
    <div
      ref={wrapperRef}
      className={`emk-preview-frame-wrap emk-preview-frame-wrap--${previewType}`}
      data-preview-type={previewType}
      style={{
        width: resolved.width || "100%",
        minWidth: resolved.minWidth,
        maxWidth: `min(${resolved.maxWidth || "100%"}, ${typeof width === "number" ? `${width}px` : width || "100%"}, 100%)`,
      }}
    >
      {/* No `allow-*` sandbox tokens -- this HTML is static markup with no
          scripts, so the strictest possible sandbox is also the correct
          one (sandboxing an iframe never restricts its own scrolling). */}
      <iframe
        className="emk-preview-document-frame"
        title={title}
        srcDoc={html}
        sandbox=""
        scrolling="auto"
        style={{
          ...iframeHeightStyle,
          minHeight: resolved.minHeight || `${minHeight}px`,
          overflowX: resolved.overflowX || "hidden",
          overflowY: resolved.overflowY || "auto",
        }}
      />
    </div>
  );
};

export default AutoHeightEmailFrame;
